/**
 * ProfilePage.jsx
 *
 * Lets a logged-in user update their own profile:
 *   - Display name
 *   - Password (re-auth handled via Firebase Auth's reauthenticate())
 *
 * The actual writes go through the `updateUserProfile` Cloud Function,
 * which mirrors changes to both Firebase Auth and the users/ Firestore
 * doc so any other view that reads displayName stays in sync.
 *
 * Email change is intentionally a read-only field for now — Firebase
 * requires verifyBeforeUpdateEmail with a verified destination, which
 * is a separate flow we haven't implemented yet.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { httpsCallable } from "firebase/functions";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { auth, functions } from "../firebaseConfig";
import { useAuth }     from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useDarkMode } from "../hooks/useDarkMode";
import Navigation      from "../components/layout/Navigation";
import {
  FiUser, FiMail, FiLock, FiBriefcase, FiAlertCircle,
  FiCheckCircle, FiSave, FiRefreshCw,
} from "react-icons/fi";

export default function ProfilePage() {
  const { user, userData, tenant }   = useAuth();
  const { t, isRTL }                 = useLanguage();
  const { darkMode }                 = useDarkMode();

  // ── form state ──────────────────────────────────────────
  const [displayName, setDisplayName] = useState(userData?.displayName || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [success, setSuccess] = useState("");

  // ── Tailwind tokens ─────────────────────────────────────
  // Same dark/light pattern used across the app — kept inline for
  // consistency and to avoid pulling in a shared theme file just for
  // one page.
  const cardCls   = darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200";
  const inputCls  = `w-full border rounded-xl px-4 py-2.5 outline-none transition-colors text-sm ${
    darkMode
      ? "bg-gray-900 border-gray-700 text-white focus:border-blue-500 placeholder-gray-500"
      : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 placeholder-gray-400"
  }`;
  const labelCls  = `block text-xs font-medium mb-1.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`;
  const headingCls = darkMode ? "text-white" : "text-gray-900";
  const subCls    = darkMode ? "text-gray-400" : "text-gray-500";

  // Has anything actually changed? Drives the save button's enabled
  // state and the "no changes" message.
  const nameChanged = displayName.trim() !== (userData?.displayName || "").trim();
  const wantsPasswordChange = newPassword.length > 0 || confirmPassword.length > 0;
  const hasChanges = nameChanged || wantsPasswordChange;

  // ── submit handler ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!hasChanges) {
      setError(t("profile_no_changes"));
      return;
    }

    // Client-side password validation. The server validates again as
    // the source of truth, but failing fast here saves a round-trip.
    if (wantsPasswordChange) {
      if (newPassword.length < 8) {
        setError(t("profile_password_too_short"));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t("profile_password_mismatch"));
        return;
      }
      if (!currentPassword) {
        setError(t("profile_current_password"));
        return;
      }
    }

    setSaving(true);
    try {
      // Firebase requires a recent re-auth for password changes. We do
      // it client-side here using the user's current password — that
      // way the server never sees the old password and we get a fresh
      // ID token before calling the Cloud Function.
      if (wantsPasswordChange) {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        try {
          await reauthenticateWithCredential(user, credential);
        } catch (reauthErr) {
          if (reauthErr.code === "auth/wrong-password" ||
              reauthErr.code === "auth/invalid-credential") {
            setError(t("profile_current_password"));
            setSaving(false);
            return;
          }
          throw reauthErr;
        }
      }

      const updateUserProfile = httpsCallable(functions, "updateUserProfile");
      const payload = {};
      if (nameChanged)            payload.displayName = displayName.trim();
      if (wantsPasswordChange)    payload.newPassword = newPassword;

      await updateUserProfile(payload);

      // Build a localized success message that reflects what actually
      // changed — saying "Password changed" when only the name moved
      // would be misleading.
      const msgs = [];
      if (nameChanged)         msgs.push(t("profile_saved"));
      if (wantsPasswordChange) msgs.push(t("profile_password_changed"));
      setSuccess(msgs.join(" • "));

      // Reset password fields so they don't persist if the user
      // navigates away and back.
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      // The Cloud Function throws HttpsError("failed-precondition", ...)
      // for "requires-recent-login". We catch it here too just in case
      // the client-side reauth somehow gets bypassed.
      if (err.code === "functions/failed-precondition") {
        setError(t("profile_reauth_required"));
      } else {
        setError(err.message || t("error"));
      }
    } finally {
      setSaving(false);
    }
  };

  // ── render ──────────────────────────────────────────────
  return (
    <div className={`min-h-screen ${darkMode
      ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white"
      : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"
    }`} dir={isRTL ? "rtl" : "ltr"}>
      <Navigation />
      <div className="p-4 md:p-8 max-w-3xl mx-auto">

        {/* ── Page header ───────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className={`text-2xl md:text-3xl font-bold ${headingCls}`}>{t("profile_title")}</h1>
          <p className={`${subCls} text-sm mt-1`}>{t("profile_subtitle")}</p>
        </motion.div>

        {/* ── Status banners ───────────────────────────── */}
        {error && (
          <div className="mb-4 flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-400">
            <FiAlertCircle className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 flex items-start gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-sm text-green-400">
            <FiCheckCircle className="flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ── Personal info card ───────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className={`${cardCls} border rounded-2xl p-6`}>
            <h2 className={`font-semibold ${headingCls} mb-4 flex items-center gap-2`}>
              <FiUser /> {t("profile_personal_info")}
            </h2>

            <div className="space-y-4">
              {/* Display name */}
              <div>
                <label className={labelCls}>{t("profile_display_name")}</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  className={inputCls}
                  maxLength={80}
                  required
                />
              </div>

              {/* Email — read-only */}
              <div>
                <label className={labelCls}>{t("profile_email")}</label>
                <div className="relative">
                  <input
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className={`${inputCls} cursor-not-allowed opacity-70`}
                  />
                  <FiMail className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "left-3" : "right-3"} ${subCls}`} />
                </div>
                <p className={`${subCls} text-xs mt-1`}>{t("profile_email_locked_hint")}</p>
              </div>

              {/* Read-only context: role + company */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("profile_role")}</label>
                  <div className={`${inputCls} flex items-center gap-2 cursor-default opacity-80`}>
                    <FiUser className={subCls} />
                    <span className="capitalize">{userData?.role || "—"}</span>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t("profile_company")}</label>
                  <div className={`${inputCls} flex items-center gap-2 cursor-default opacity-80`}>
                    <FiBriefcase className={subCls} />
                    <span className="truncate">{tenant?.name || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── Security card ────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className={`${cardCls} border rounded-2xl p-6`}>
            <h2 className={`font-semibold ${headingCls} mb-1 flex items-center gap-2`}>
              <FiLock /> {t("profile_security")}
            </h2>
            <p className={`${subCls} text-xs mb-4`}>{t("profile_password_hint")}</p>

            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t("profile_current_password")}</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className={inputCls}
                  autoComplete="current-password"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t("profile_new_password")}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className={inputCls}
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className={labelCls}>{t("profile_confirm_password")}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className={inputCls}
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── Submit ───────────────────────────────────── */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || !hasChanges}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                saving || !hasChanges
                  ? "bg-gray-500/20 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {saving ? <FiRefreshCw className="animate-spin" /> : <FiSave />}
              {t("profile_save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
